import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { setDebugTime, getDebugTime, FIRST_KICKOFF_UTC } from '../utils/lockUtils';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  createSandboxLeague,
  resetSandbox,
  deleteSandbox,
  setMatchResult,
  clearMatchResult,
  updateRoundStatus,
  executeAccountDeletion,
} from '../services/adminService';
import './AdminPage.css';

const ROUND_ORDER  = ['group', 'r32', 'r16', 'qf', 'sf', 'final', 'third'];
const ROUND_LABELS = { group: 'Group Stage', r32: 'Round of 32', r16: 'Round of 16', qf: 'Quarterfinals', sf: 'Semifinals', final: 'Final', third: '3rd Place' };
const STATUS_OPTIONS = ['pending', 'open', 'locked', 'complete'];

export default function AdminPage() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  // Redirect non-admins
  useEffect(() => {
    if (userProfile && userProfile.role !== 'admin') navigate('/leagues', { replace: true });
  }, [userProfile, navigate]);

  const [sandboxLeagueId, setSandboxLeagueId] = useState(null);
  const [rounds, setRounds]                   = useState([]);
  const [matches, setMatches]                 = useState([]);
  const [teams, setTeams]                     = useState({});

  // Sandbox form state
  const [sandboxLoading, setSandboxLoading]   = useState(false);
  const [sandboxMsg, setSandboxMsg]           = useState('');

  // Round control state
  const [roundLoading, setRoundLoading]       = useState({});

  // Inject result form state
  const [selectedRound, setSelectedRound]     = useState('group');
  const [selectedMatch, setSelectedMatch]     = useState('');
  const [homeScore, setHomeScore]             = useState('');
  const [awayScore, setAwayScore]             = useState('');
  const [winner, setWinner]                   = useState('home');
  const [injectLoading, setInjectLoading]     = useState(false);
  const [injectMsg, setInjectMsg]             = useState('');
  const [clearLoading, setClearLoading]       = useState(false);

  // Debug time override
  const [debugTimeInput, setDebugTimeInput]   = useState('');
  const [debugTimeActive, setDebugTimeActive] = useState(!!getDebugTime());

  // Pending deletions
  const [pendingDeletions, setPendingDeletions]   = useState([]);
  const [deletionsLoading, setDeletionsLoading]   = useState(true);
  const [executingDeletion, setExecutingDeletion] = useState({});
  const [deletionMsg, setDeletionMsg]             = useState({});

  // Reset/delete sandbox state
  const [resetLoading, setResetLoading]       = useState(false);
  const [resetMsg, setResetMsg]               = useState('');
  const [deleteLoading, setDeleteLoading]     = useState(false);

  // Load sandbox league ID from settings
  useEffect(() => {
    getDoc(doc(db, 'settings', 'app')).then((snap) => {
      if (snap.exists()) setSandboxLeagueId(snap.data().sandboxLeagueId ?? null);
    });
  }, []);

  // Real-time rounds
  useEffect(() => {
    return onSnapshot(collection(db, 'rounds'), (snap) => {
      const data = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => ROUND_ORDER.indexOf(a.id) - ROUND_ORDER.indexOf(b.id));
      setRounds(data);
    }, (err) => console.error('rounds subscription error:', err));
  }, []);

  // All matches + teams
  useEffect(() => {
    getDocs(collection(db, 'matches')).then((snap) => {
      setMatches(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    getDocs(collection(db, 'teams')).then((snap) => {
      const map = {};
      snap.docs.forEach((d) => { map[d.id] = d.data(); });
      setTeams(map);
    });
  }, []);

  // Pending account deletions
  useEffect(() => {
    getDocs(query(collection(db, 'pendingDeletions'), where('status', '==', 'pending')))
      .then((snap) => {
        setPendingDeletions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch(() => {})
      .finally(() => setDeletionsLoading(false));
  }, []);

  const matchesForRound = matches
    .filter((m) => m.round === selectedRound)
    .sort((a, b) => (a.kickoffTime?.seconds ?? 0) - (b.kickoffTime?.seconds ?? 0));

  const selectedMatchData = matches.find((m) => m.id === selectedMatch);

  /* ── Handlers ── */

  function handleSetDebugTime(e) {
    e.preventDefault();
    if (!debugTimeInput) return;
    setDebugTime(new Date(debugTimeInput));
    setDebugTimeActive(true);
  }

  function handleClearDebugTime() {
    setDebugTime(null);
    setDebugTimeInput('');
    setDebugTimeActive(false);
  }

  async function handleCreateSandbox() {
    setSandboxLoading(true);
    setSandboxMsg('');
    try {
      const id = await createSandboxLeague(currentUser.uid);
      setSandboxLeagueId(id);
      setSandboxMsg('Sandbox league created and seeded.');
    } catch (e) {
      setSandboxMsg(`Error: ${e.message}`);
    } finally {
      setSandboxLoading(false);
    }
  }

  async function handleRoundStatus(roundId, status) {
    setRoundLoading((prev) => ({ ...prev, [roundId]: true }));
    try {
      await updateRoundStatus(roundId, status);
    } finally {
      setRoundLoading((prev) => ({ ...prev, [roundId]: false }));
    }
  }

  async function handleInjectResult(e) {
    e.preventDefault();
    if (!selectedMatch) return;
    setInjectLoading(true);
    setInjectMsg('');
    try {
      const hs = parseInt(homeScore, 10);
      const as = parseInt(awayScore, 10);
      if (isNaN(hs) || isNaN(as)) throw new Error('Scores must be numbers.');

      let winnerId = null;
      if (winner === 'home')  winnerId = selectedMatchData.homeTeamId;
      if (winner === 'away')  winnerId = selectedMatchData.awayTeamId;
      // 'draw' → winnerId stays null

      await setMatchResult(selectedMatch, { homeScore: hs, awayScore: as, winnerId });
      setInjectMsg('Result injected — scoring will process automatically.');
      setHomeScore('');
      setAwayScore('');
    } catch (e) {
      setInjectMsg(`Error: ${e.message}`);
    } finally {
      setInjectLoading(false);
    }
  }

  async function handleClearResult() {
    if (!selectedMatch || clearLoading) return;
    const matchLabel = selectedMatchData
      ? `${teams[selectedMatchData.homeTeamId]?.name ?? 'Home'} vs ${teams[selectedMatchData.awayTeamId]?.name ?? 'Away'}`
      : selectedMatch;
    if (!window.confirm(`Undo result for ${matchLabel}?\n\nThis will reverse all points earned from this match across every league.`)) return;
    setClearLoading(true);
    setInjectMsg('');
    try {
      await clearMatchResult(selectedMatch);
      setInjectMsg('Result cleared — points reversed, match reset to locked.');
      // Refresh match list so ✓ indicator updates
      const snap = await import('firebase/firestore').then(({ getDocs, collection }) =>
        getDocs(collection(db, 'matches'))
      );
      setMatches(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setInjectMsg(`Error: ${e.message}`);
    } finally {
      setClearLoading(false);
    }
  }

  async function handleResetSandbox() {
    if (!sandboxLeagueId) return;
    if (!window.confirm('Reset sandbox? This will wipe all fake picks and re-seed.')) return;
    setResetLoading(true);
    setResetMsg('');
    try {
      await resetSandbox(sandboxLeagueId);
      setResetMsg('Sandbox reset and re-seeded.');
    } catch (e) {
      setResetMsg(`Error: ${e.message}`);
    } finally {
      setResetLoading(false);
    }
  }

  async function handleDeleteSandbox() {
    if (!sandboxLeagueId) return;
    if (!window.confirm('Delete sandbox entirely? This permanently removes the league, all fake users, and all picks. Cannot be undone.')) return;
    setDeleteLoading(true);
    setResetMsg('');
    try {
      await deleteSandbox(sandboxLeagueId);
      setSandboxLeagueId(null);
      setResetMsg('Sandbox deleted.');
    } catch (e) {
      setResetMsg(`Error: ${e.message}`);
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleExecuteDeletion(uid) {
    if (executingDeletion[uid]) return;
    if (!window.confirm(`Execute account deletion for ${uid}?\n\nThis permanently deletes all Firestore data. You must also delete the Firebase Auth account manually in the Firebase console.`)) return;
    setExecutingDeletion((prev) => ({ ...prev, [uid]: true }));
    setDeletionMsg((prev) => ({ ...prev, [uid]: '' }));
    try {
      await executeAccountDeletion(uid);
      setPendingDeletions((prev) => prev.filter((d) => d.id !== uid));
      setDeletionMsg((prev) => ({ ...prev, [uid]: 'Firestore data deleted. Delete the Auth account in Firebase console.' }));
    } catch (e) {
      setDeletionMsg((prev) => ({ ...prev, [uid]: `Error: ${e.message}` }));
    } finally {
      setExecutingDeletion((prev) => ({ ...prev, [uid]: false }));
    }
  }

  if (!userProfile) return null;

  return (
    <div className="admin-page">
      <header className="admin-header">
        <span className="admin-header-title display-font">Admin Console</span>
        <button className="admin-header-btn" onClick={() => navigate('/admin/blog')}>
          Blog
        </button>
        <button className="admin-header-btn" onClick={() => navigate('/leagues')}>
          ← Leagues
        </button>
      </header>

      <div className="admin-body">

        {/* ── Sandbox League ── */}
        <section className="admin-section">
          <h2 className="admin-section-title">Sandbox League</h2>
          {sandboxLeagueId ? (
            <div className="admin-card">
              <div className="admin-field-row">
                <span className="admin-label">Sandbox ID</span>
                <code className="admin-code">{sandboxLeagueId}</code>
              </div>
              <div className="admin-btn-row">
                <button
                  className="admin-btn admin-btn--secondary"
                  onClick={() => navigate(`/league/${sandboxLeagueId}`)}
                >
                  View League
                </button>
                <button
                  className="admin-btn admin-btn--danger"
                  onClick={handleResetSandbox}
                  disabled={resetLoading || deleteLoading}
                >
                  {resetLoading ? 'Resetting…' : 'Reset Sandbox'}
                </button>
                <button
                  className="admin-btn admin-btn--danger"
                  onClick={handleDeleteSandbox}
                  disabled={deleteLoading || resetLoading}
                >
                  {deleteLoading ? 'Deleting…' : 'Delete Sandbox'}
                </button>
              </div>
              {resetMsg && <p className="admin-msg">{resetMsg}</p>}
            </div>
          ) : (
            <div className="admin-card">
              <p className="admin-hint">No sandbox league exists yet.</p>
              <button
                className="admin-btn admin-btn--primary"
                onClick={handleCreateSandbox}
                disabled={sandboxLoading}
              >
                {sandboxLoading ? 'Creating…' : 'Create Sandbox League'}
              </button>
              {sandboxMsg && <p className="admin-msg">{sandboxMsg}</p>}
            </div>
          )}
        </section>

        {/* ── Round Controls ── */}
        <section className="admin-section">
          <h2 className="admin-section-title">Round Controls</h2>
          <div className="admin-card admin-card--table">
            {rounds.map((round) => (
              <div key={round.id} className="admin-round-row">
                <span className="admin-round-name">{ROUND_LABELS[round.id] ?? round.id}</span>
                <span className={`admin-round-status admin-round-status--${round.status}`}>
                  {round.status}
                </span>
                <div className="admin-round-btns">
                  {STATUS_OPTIONS.filter((s) => s !== round.status).map((s) => (
                    <button
                      key={s}
                      className="admin-btn admin-btn--sm"
                      onClick={() => handleRoundStatus(round.id, s)}
                      disabled={roundLoading[round.id]}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Inject Result ── */}
        <section className="admin-section">
          <h2 className="admin-section-title">Inject Match Result</h2>
          <form className="admin-card" onSubmit={handleInjectResult}>
            <div className="admin-field">
              <label className="admin-label">Round</label>
              <select
                className="admin-select"
                value={selectedRound}
                onChange={(e) => { setSelectedRound(e.target.value); setSelectedMatch(''); }}
              >
                {ROUND_ORDER.map((r) => (
                  <option key={r} value={r}>{ROUND_LABELS[r]}</option>
                ))}
              </select>
            </div>

            <div className="admin-field">
              <label className="admin-label">Match</label>
              <select
                className="admin-select"
                value={selectedMatch}
                onChange={(e) => setSelectedMatch(e.target.value)}
              >
                <option value="">— select match —</option>
                {matchesForRound.map((m) => (
                  <option key={m.id} value={m.id}>
                    {teams[m.homeTeamId]?.name ?? m.homeTeamId} vs {teams[m.awayTeamId]?.name ?? m.awayTeamId}
                    {m.status === 'final' ? ' ✓' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="admin-score-row">
              <div className="admin-field">
                <label className="admin-label">
                  {selectedMatchData ? (teams[selectedMatchData.homeTeamId]?.name ?? 'Home') : 'Home'} Score
                </label>
                <input
                  className="admin-input"
                  type="number"
                  min="0"
                  value={homeScore}
                  onChange={(e) => setHomeScore(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="admin-field">
                <label className="admin-label">
                  {selectedMatchData ? (teams[selectedMatchData.awayTeamId]?.name ?? 'Away') : 'Away'} Score
                </label>
                <input
                  className="admin-input"
                  type="number"
                  min="0"
                  value={awayScore}
                  onChange={(e) => setAwayScore(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="admin-field">
              <label className="admin-label">Winner</label>
              <div className="admin-radio-row">
                <label className="admin-radio">
                  <input type="radio" value="home" checked={winner === 'home'} onChange={() => setWinner('home')} />
                  {selectedMatchData ? (teams[selectedMatchData.homeTeamId]?.name ?? 'Home') : 'Home'}
                </label>
                {selectedRound === 'group' && (
                  <label className="admin-radio">
                    <input type="radio" value="draw" checked={winner === 'draw'} onChange={() => setWinner('draw')} />
                    Draw
                  </label>
                )}
                <label className="admin-radio">
                  <input type="radio" value="away" checked={winner === 'away'} onChange={() => setWinner('away')} />
                  {selectedMatchData ? (teams[selectedMatchData.awayTeamId]?.name ?? 'Away') : 'Away'}
                </label>
              </div>
            </div>

            <div className="admin-btn-row">
              <button
                type="submit"
                className="admin-btn admin-btn--primary"
                disabled={!selectedMatch || homeScore === '' || awayScore === '' || injectLoading || clearLoading}
              >
                {injectLoading ? 'Injecting…' : 'Inject Result'}
              </button>
              {selectedMatchData?.status === 'final' && (
                <button
                  type="button"
                  className="admin-btn admin-btn--danger"
                  onClick={handleClearResult}
                  disabled={clearLoading || injectLoading}
                  title="Reverse all points earned from this match and reset it to locked"
                >
                  {clearLoading ? 'Clearing…' : 'Clear Result'}
                </button>
              )}
            </div>
            {injectMsg && <p className="admin-msg">{injectMsg}</p>}
          </form>
        </section>

        {/* ── Debug Time Override ── */}
        <section className="admin-section">
          <h2 className="admin-section-title">Debug Time Override</h2>
          <div className="admin-card">
            <p className="admin-hint">
              Simulate a different current time to test locking, countdown, and invite expiry.
              Stored in <code>localStorage</code> — only affects this browser.
            </p>
            <div className="admin-field">
              <label className="admin-label">First kickoff (real): {FIRST_KICKOFF_UTC.toLocaleString()}</label>
            </div>
            {debugTimeActive && (
              <div className="admin-field">
                <span className="admin-label" style={{ color: '#f9a825', fontWeight: 700 }}>
                  ⚠ Override active: {getDebugTime()?.toLocaleString()}
                </span>
              </div>
            )}
            <form className="admin-debug-row" onSubmit={handleSetDebugTime}>
              <input
                className="admin-input"
                type="datetime-local"
                value={debugTimeInput}
                onChange={(e) => setDebugTimeInput(e.target.value)}
              />
              <button type="submit" className="admin-btn admin-btn--primary" disabled={!debugTimeInput}>
                Set Time
              </button>
              {debugTimeActive && (
                <button type="button" className="admin-btn admin-btn--secondary" onClick={handleClearDebugTime}>
                  Clear Override
                </button>
              )}
            </form>
          </div>
        </section>

        {/* ── Pending Account Deletions ── */}
        <section className="admin-section">
          <h2 className="admin-section-title">Pending Account Deletions</h2>
          <div className="admin-card">
            {deletionsLoading ? (
              <p className="admin-hint">Loading…</p>
            ) : pendingDeletions.length === 0 ? (
              <p className="admin-hint">No pending deletions.</p>
            ) : (
              pendingDeletions.map((pd) => {
                const executeAt = pd.executeAt
                  ? new Date(pd.executeAt.seconds * 1000).toLocaleString()
                  : '—';
                return (
                  <div key={pd.id} className="admin-round-row">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <code className="admin-code">{pd.id}</code>
                      <div className="admin-hint" style={{ marginTop: 4 }}>
                        Scheduled: {executeAt} · Leagues: {pd.leagueIds?.length ?? 0}
                      </div>
                      {deletionMsg[pd.id] && (
                        <p className="admin-msg" style={{ marginTop: 4 }}>{deletionMsg[pd.id]}</p>
                      )}
                    </div>
                    <button
                      className="admin-btn admin-btn--danger admin-btn--sm"
                      onClick={() => handleExecuteDeletion(pd.id)}
                      disabled={!!executingDeletion[pd.id]}
                    >
                      {executingDeletion[pd.id] ? 'Executing…' : 'Execute'}
                    </button>
                  </div>
                );
              })
            )}
            <p className="admin-hint" style={{ borderTop: '1px solid var(--surface3)', paddingTop: 10 }}>
              After executing, delete the Firebase Auth account manually in the Firebase console.
              Automatic execution at the scheduled time requires a Cloud Function.
            </p>
          </div>
        </section>

      </div>
    </div>
  );
}
